import { Component, computed, effect, inject, signal } from '@angular/core';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DirectoryService } from '@/services/api/directory.service';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DirectoryUser, JobTag, PhoneType, UserPhone } from '@/models/directory-user.model';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { TagModule } from 'primeng/tag';
import { PhoneFormatPipe } from "../../pipes/db-phone-format.pipe";
import { ChipModule } from 'primeng/chip';
import { FloatLabelModule } from 'primeng/floatlabel';
import { PermRole } from '@/models/user-data.model';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputMaskModule } from 'primeng/inputmask';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { PanelModule } from 'primeng/panel';

import { DirectoryAccessService } from '@/services/app/directory-access.service';
import { PermsListComponent } from './perms-list.component';
import { UserRolesEditorService } from './user-roles-editor.service';

interface PhoneColumn {
  number: string;
  type: PhoneType;
}

@Component({
    selector: 'app-directory-form',
    imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    ReactiveFormsModule,
    AvatarModule,
    AvatarGroupModule,
    TagModule,
    ChipModule,
    FloatLabelModule,
    MultiSelectModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputMaskModule,
    SelectModule,
    ToggleButtonModule,
    PanelModule,
    PhoneFormatPipe,
    PermsListComponent
],
styles: `
  :host ::ng-deep .profile {
      border-radius: var(--border-radius) !important;
  }
  :host ::ng-deep .g-left {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
  :host ::ng-deep .g-right {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
  }
`,
template: `

<div class="flex flex-col gap-6">
        <!-- First Section: Image and Name, Category, Tech -->
        <div class="flex gap-3">
            
            <div class="grid grid-cols-12 gap-6" >
                <div class="col-span-6">
                    <div class="flex gap-4 items-start">
                        <div>
                            <p-avatar *ngIf="user?.photoUrl" styleClass="mr-2" size="large" [image]="user?.photoUrl" size="xlarge"/>
                            <p-avatar *ngIf="!user?.photoUrl" [label]="directoryService.getInitials(user)" styleClass="mr-2" size="xlarge" />
                        </div>
                        <div class="flex-1">
                            <label for="name">Name</label>
                            <p-inputgroup>
                                <input type="text" pInputText id="name" required fluid [ngModel]="editingUser?.FirstName" (ngModelChange)="editingUser!.FirstName = $event" [disabled]="!directoryAccessService.globalUnlocked()"/>
                                <input type="text" pInputText id="name" required fluid [ngModel]="editingUser?.LastName" (ngModelChange)="editingUser!.LastName = $event" [disabled]="!directoryAccessService.globalUnlocked()"/>
                            </p-inputgroup>
                        </div>
                    </div>
                </div>
                <div class="col-span-6">
                <label for="description">Title</label>
                <input type="text" pInputText id="imageUrl" fluid [ngModel]="editingUser?.Title" (ngModelChange)="editingUser!.Title = $event"/>
            </div>
            </div>
        </div>

        <!-- Second Section: Two Columns Layout -->
        <div class="grid grid-cols-12 gap-6" >
        @if(directoryAccessService.globalUnlocked()) {
       <div class="col-span-6">

        <label for="category">Status</label>
                <div class="flex w-full">
                    <p-togglebutton class="flex-1 w-full" [ngModel]="editingUser?.Archived" onIcon="pi pi-times" offIcon="pi pi-check" (ngModelChange)="editingUser!.Archived = $event" onLabel="Archived" offLabel="Active" styleClass="g-left w-full"/>
                    <p-togglebutton class="flex-1 w-full" [ngModel]="editingUser?.PartTime" onIcon="pi pi-clock" offIcon="pi pi-check" (ngModelChange)="editingUser!.PartTime = $event" onLabel="Part-Time" offLabel="Full-Time" styleClass="g-right w-full"/>
                </div>
            </div>
            <div class="col-span-6" >
                <label for="description">Locations</label>
                <p-multiselect [options]="directoryService.officePerms()" [filter]="true" filterBy="Franchises.CityCode" [(ngModel)]="selectedLocations" (ngModelChange)="locationsChanged = true" placeholder="Select Offices" optionLabel="Franchises.CityCode" display="chip" fluid />
            </div>
            <div class="col-span-6">
                <label for="description">Roles</label>
                <p-multiselect [options]="directoryService.availableTags()" [filter]="true" filterBy="name" [(ngModel)]="selectedTags" (ngModelChange)="tagsChanged = true" placeholder="Select Tags" optionLabel="name" display="chip" fluid />
            </div>
        }
            <div class="col-span-6">
                <label for="description">Address</label>
                <input type="text" pInputText id="imageUrl" fluid [ngModel]="editingUser?.Address1" (ngModelChange)="editingUser!.Address1 = $event"/>
                <input type="text" pInputText id="imageUrl" fluid [ngModel]="editingUser?.Address2" (ngModelChange)="editingUser!.Address2 = $event"/>
            </div>
            <div class="col-span-6">
                <label for="tag">Phones</label>
                <div class="flex gap-0">
                  <p-inputmask mask="(999) 999-9999"  [(ngModel)]="newNumber" placeholder="(999) 999-9999" class="g-left" [style]="{'border-bottom-right-radius': '0px', 'border-top-right-radius': '0px', 'height': '37px'}"/>
                <p-inputgroup>
    <p-select [options]="directoryService.phoneTypes" class="g-left" size="small" [(ngModel)]="newPhoneType" optionLabel="name" placeholder="Type" [style]="{'border-bottom-left-radius': '0px', 'border-top-left-radius': '0px', 'height': '37px'}"  />
    <p-inputgroup-addon> <p-button  icon="pi pi-plus" class="p-button-success" (onClick)="onAddPhone()" [disabled]="!newNumber || !newPhoneType" /></p-inputgroup-addon>
</p-inputgroup>
</div>
<p-chip *ngFor="let phone of editingUser?.user_phones" [label]="phone.number | phoneFormat" [icon]="getPhoneIcon(phone.phone_type_id)" [removable]="true" (onRemove)="onRemovePhone(phone)" />
            </div>
            @if(directoryAccessService.globalUnlocked()) {
            <div class="col-span-6">
                <label for="book" class="block font-bold mb-3">Aliases</label>
                <p-multiselect [options]="directoryService.userList()" [showToggleAll]="false" [filterBy]="'DisplayName,User'" [filterPlaceHolder]="'Search'" [(ngModel)]="selectedAliases" (ngModelChange)="aliasesChanged = true" placeholder="Find Aliases" optionLabel="DisplayName" appendTo="body" fluid >
                <ng-template let-user #item>
        <div class="flex items-center gap-2">
        <p-avatar *ngIf="user.photoUrl" shape="circle" [image]="user.photoUrl"/>
        <p-avatar *ngIf="!user.photoUrl" [label]="directoryService.getInitials(user)" shape="circle" />
            <div>{{ user.DisplayName }}</div>
        </div>
    </ng-template>
    </p-multiselect>
            </div>
            }
            <div class="col-span-6">
                <label for="tag" class="block font-bold mb-3">Manager</label>
                <p-select [options]="directoryService.userList()" appendTo="body" [(ngModel)]="selectedManager" (ngModelChange)="managerChanged = true" optionLabel="DisplayName" [filter]="true" filterBy="DisplayName,User" [showClear]="true" placeholder="Find A Manager" fluid>
                <ng-template let-user #item>
        <div class="flex items-center gap-2">
        <p-avatar *ngIf="user.photoUrl" shape="circle" [image]="user.photoUrl"/>
        <p-avatar *ngIf="!user.photoUrl" [label]="directoryService.getInitials(user)" shape="circle" />
            <div>{{ user.DisplayName }}</div>
        </div>
    </ng-template>
    </p-select>
            </div>
            </div>
<p-panel header="Permissions" #permPanel [toggler]="'header'" [toggleable]="true" [collapsed]="hidePerms">
    <app-perms-list (roleRemoved)="onRemoveRole($event)"></app-perms-list>
</p-panel>
            <div class="col-span-12" >
                <p-button label="Save" icon="pi pi-save" iconPos="right" class="p-button-success" (onClick)="onSave()" [loading]="directoryService.formLoading()" [disabled]="directoryService.formLoading()"/>
            </div>

    </div>

`
})
export class DirectoryFormComponent {
  public directoryService = inject(DirectoryService);
  public directoryAccessService = inject(DirectoryAccessService);
  private userRolesEditorService = inject(UserRolesEditorService);
    editingUser: DirectoryUser | null = null;
    selectedLocations: PermRole[] = [];
    newNumber: string = '';
    newPhoneType: PhoneType | null = null;
    selectedAliases: DirectoryUser[] = [];
    selectedManager: DirectoryUser | null = null;
    hidePerms: boolean = false;

    phonesChanged = false;
    aliasesChanged = false;
    locationsChanged = false;
    managerChanged = false;
    tagsChanged = false;
    selectedTags: JobTag[] = [];

    availableTags = computed(() => {
      if(!this.editingUser || !this.userTags) {
        return this.directoryService.availableTags()
      }
      return this.directoryService.availableTags().filter(t => t.id in this.userTags.map(t => t.tag_id) || []);
    });

  constructor(

    
  ) {
    effect(() => {
      const user = this.directoryService.selectedUser();
      if (user) {
        this.editingUser = user;
        this.setAliases(user);
        this.setManager(user);
        this.hidePerms = true;
        this.selectedLocations = this.directoryService.officePerms().filter(p => user.UserRoles?.some(r => r.PermRoles?.FID === p.FID));
        // Reset change flags when loading a new user
        this.phonesChanged = false;
        this.aliasesChanged = false;
        this.locationsChanged = false;
        this.managerChanged = false;
        this.tagsChanged = false;
        this.selectedTags = this.userTags.map(t => t.jobTags);
      }
    });
  }

  get userTags() {
    return this.editingUser?.user_tags_user_tags_uidToUsers || [];
  }

  get user() {
    return this.editingUser || this.directoryService.selectedUser();
  }

  onRemoveRole(role: any) {
    console.log('remove role', role);
  }

  processPhones(phones: UserPhone[]) {
    return phones.map(p => {
      return {
        is_active: p.is_active,
        created_at: p.created_at,
        uid: p.uid,
        phone_type_id: p.phone_type_id,
        number: p.number,
        extension: p.extension,
        is_primary: p.is_primary,
      }
    })
  }

  onSave() {
    if(!this.editingUser) {
      return;
    }

    let phones:any[] = [];
if(this.editingUser?.user_phones) {
    phones = this.processPhones(this.editingUser?.user_phones || []);
}
let rolesAdded: number[] = [];
rolesAdded = this.userRolesEditorService.selectedUserRoles().filter(r => r.UserRoleID === 0).map(r => r.PermRoleID);
const rolesRemoved = this.userRolesEditorService.removedRoles();

console.log('roles added', rolesAdded);
console.log('roles removed', rolesRemoved);
    let body: any = {
      user: {
        FirstName: this.editingUser.FirstName,
        LastName: this.editingUser.LastName,
        DisplayName: this.editingUser.FirstName + ' ' + this.editingUser.LastName,
        Title: this.editingUser.Title,
        PartTime: this.editingUser.PartTime,
        Archived: this.editingUser.Archived,
        Address1: this.editingUser.Address1,
        Address2: this.editingUser.Address2,
        modifiedDate: new Date(),
        manager_uid: this.selectedManager?.UID || null,
      },
      phones: phones,
      aliases: this.selectedAliases.map(a => a.UID),
      locations: this.selectedLocations.map(l => l.PermRoleID),
      tags: this.selectedTags.map(t => t.id),
      rolesAdded: rolesAdded,
      rolesRemoved: rolesRemoved.map(r => r.PermRoleID),
    }


    console.log('save', body);
    const id = this.editingUser?.UID;
    if(!id) {
      return;
    }
    this.directoryService.updateUser(id, body);
    this.directoryService.showEditForm.set(false);
  }

  onAddPhone() {
    if(!this.newPhoneType || !this.newNumber) {
      return;
    }
    const newPhone: UserPhone = {
      number: this.newNumber,
      phoneType: this.newPhoneType,
      phone_type_id: this.newPhoneType.id,
      extension: '',
      is_active: true,
      created_at: new Date(),
      id: 0,
      uid: 0,
      is_primary: false,
    }
    this.editingUser?.user_phones.push(newPhone);
    this.phonesChanged = true;
    this.newNumber = '';
    this.newPhoneType = null;
  }

  setAliases(user: DirectoryUser) {
    const aliases = this.directoryService.userList().filter(u => user.other_Users_Users_alias_of_uidToUsers.some(a => a.UID === u.UID));
    this.selectedAliases = aliases
  }

  setManager(user: DirectoryUser) {
    if(user.Users_Users_manager_uidToUsers) {
      const manager = this.directoryService.userList().find(u => u.UID === user.Users_Users_manager_uidToUsers.UID);
      this.selectedManager = manager || null;
    }
    else {
      this.selectedManager = null;
    }
  }

  onRemovePhone(phone: UserPhone) {
    if(this.editingUser?.user_phones) {
      const index = this.editingUser.user_phones.indexOf(phone);
      if(index > -1) {
        this.editingUser.user_phones.splice(index, 1);
        this.phonesChanged = true;
      }
    }
  }

  getPhoneIcon(phone: number) {
    if(phone === 1) {
    return 'pi pi-briefcase';
    }
    else if(phone === 2) {
      return 'pi pi-mobile';
    }
    else if(phone === 3) {
      return 'pi pi-home';
    }
    return 'pi pi-phone';
  }
}
